import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import type { ITranslation } from '@/interface/ITranslation';
import type { SportType } from '@/interface/enum';
import { LanguageType, RoutePage } from '@/interface/enum';
import { getOrderedSimulationSportList } from '@/libraries/helpers/sportsHelper';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { useAccountStore } from '@/stores/accountStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

const tournamentCenterRoutes: string[] = [
    RoutePage.TournamentCenterOverview,
    RoutePage.TournamentCenterCountries,
    RoutePage.TournamentCenterMatches,
    RoutePage.TournamentCenterPromotion,
];

export const useZone = () => {
    const {
        eFootballZoneLeagueIds,
        sportsCupTournamentNames,
        multipleSportsTournamentNames,
        tournamentCenterLeagueNames,
        simulationSportsZoneLeagueIds,
    } = storeToRefs(useFeatureToggleStore());

    const { sportType } = storeToRefs(useRootStore());
    const { customerInfo } = storeToRefs(useAccountStore());
    const { multipleSportsZoneSupportSports } = storeToRefs(useFeatureToggleStore());

    const route = useRoute();
    const isInEFootballZone = computed(() => route.name === RoutePage.EFootballPage);
    const isInSportsCupZone = computed(() => route.name === RoutePage.SportsCupPage);
    const isInMultipleSportsZone = computed(() => route.name === RoutePage.MultipleSportsZone);
    const isInSimulationSportsZone = computed(() => route.name === RoutePage.SimulationSportsZone);
    const isInLeagueZone = computed(() => route.name === RoutePage.MainBySport
     && !!((route.query.leagueNames as string)?.trim()
      || (route.query.leagueIds as string)?.trim()));
    const isTournamentCenterZone = computed(() => !!route.name && tournamentCenterRoutes.includes(route.name.toString()));
    const isInTournamentCenterMatches = computed(() => route.name === RoutePage.TournamentCenterMatches);
    const isInTournamentCenterCountries = computed(() => route.name === RoutePage.TournamentCenterCountries);

    const isInAnyZone = computed(() => [
        isInEFootballZone.value,
        isInSportsCupZone.value,
        isInMultipleSportsZone.value,
        isInSimulationSportsZone.value,
        isInLeagueZone.value,
        isTournamentCenterZone.value,
    ].some(Boolean));

    const sportList = computed<SportType[]>(() => {
        if (isInMultipleSportsZone.value) {
            return multipleSportsZoneSupportSports.value;
        }

        if (isInSimulationSportsZone.value) {
            return getOrderedSimulationSportList(simulationSportsZoneLeagueIds.value, customerInfo.value.currency);
        }

        return [];
    });

    const showOutright = computed(() => isInMultipleSportsZone.value);
    const shouldShowNoMatchesMarketSwitchButton = computed(() => !isInMultipleSportsZone.value && !isInSimulationSportsZone.value);

    /*
     * Sports cup zone and multiple sports zone only use league name
     */
    const leagueNames = computed(() => {
        if (isInSportsCupZone.value) {
            return sportsCupTournamentNames.value;
        }

        if (isInMultipleSportsZone.value) {
            return multipleSportsTournamentNames.value;
        }

        if (isTournamentCenterZone.value) {
            return tournamentCenterLeagueNames.value;
        }

        const leagueNames = (route.query.leagueNames as string)?.trim();
        if (leagueNames) {
            return leagueNames.split(',').map(x => x.trim());
        }

        return undefined;
    });

    /*
     * EFootball zone and SimulationSports zone use league id
     */
    const leagueIds = computed(() => {
        if (isInEFootballZone.value) {
            return eFootballZoneLeagueIds.value;
        }

        if (isInSimulationSportsZone.value) {
            return simulationSportsZoneLeagueIds.value[sportType.value] ?? [];
        }

        const leagueIds = (route.query.leagueIds as string)?.trim();
        if (leagueIds) {
            return leagueIds.split(',').map(x => Number.parseInt(x.trim()));
        }

        return undefined;
    });

    const zoneTitleKey = computed(() => {
        if (isInSportsCupZone.value) {
            return 'sportsCupZone_title';
        }

        if (isInMultipleSportsZone.value) {
            return 'multipleSports_title';
        }

        if (isInEFootballZone.value) {
            return 'efootball_title';
        }

        if (isInSimulationSportsZone.value) {
            return 'promotion_zone_7';
        }

        if (isTournamentCenterZone.value) {
            return 'tournament_center_event_title';
        }

        if (isInLeagueZone.value) {
            return 'featured_events_title';
        }

        return undefined;
    });

    function isSportsCupLeague(leagueNameEn: string): boolean {
        return sportsCupTournamentNames.value.some(x => leagueNameEn.toUpperCase().includes(x.toUpperCase()));
    }

    return {
        isTournamentCenterZone,
        isInEFootballZone,
        isInSportsCupZone,
        isInMultipleSportsZone,
        isInSimulationSportsZone,
        isInLeagueZone,
        isInAnyZone,
        leagueNames,
        leagueIds,
        zoneTitleKey,
        sportList,
        showOutright,
        shouldShowNoMatchesMarketSwitchButton,
        isInTournamentCenterMatches,
        isInTournamentCenterCountries,
        isSportsCupLeague,
    };
};

export function isShowTournamentCenterMatchInfo(leagueName: ITranslation[] | undefined | string): boolean {
    const { isTournamentCenterEnabled, tournamentCenterLeagueNames } = storeToRefs(useFeatureToggleStore());
    if (!isTournamentCenterEnabled.value) {
        return false;
    }

    const leagueNameEn = getTranslated(leagueName, LanguageType.EN);
    if (tournamentCenterLeagueNames.value.some(x => leagueNameEn.includes(x))) {
        return true;
    }

    return false;
}
